import { Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { medicationApi } from 'src/api/medication';
import { reminderApi } from 'src/api/reminder'; // still needed for reminder CRUD mutations
import Iconify from 'src/components/iconify';
import ProfileMedicationsDeleteModal from 'src/sections/@dashboard/profile/components/ProfileMedicationsDeleteModal';
import MedicationFormModal, {
  MedicationsFormModalValuesProps,
} from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import ProfileMedicationsTable from 'src/sections/@dashboard/profile/components/ProfileMedicationsTable';
import ProfileMedicationsTableSkeleton from 'src/sections/@dashboard/profile/components/ProfileMedicationsTableSkeleton';
import MedicationSourceVisitModal from 'src/sections/@dashboard/profile/components/MedicationSourceVisitModal';
import ProfileMedicationsFilters, {
  DEFAULT_MEDICATION_FILTERS,
  MedicationFilters,
} from 'src/sections/@dashboard/profile/components/ProfileMedicationsFilters';
import {
  buildMedicationProfilePayload,
  buildStatusChangePayload,
} from 'src/sections/@dashboard/profile/components/medication-form/medicationFormPayload';
import {
  AddProfileMedicationDto,
  ChangeMedicationStatusDto,
  CreateReminderDto,
  CreateReminderDtoNotificationChannelsEnum,
  GetProfileMedicationsDtoLinkedReminder,
  UpdateProfileMedicationDto,
  UpdateReminderDto,
} from 'woofwoof-api';
import { FilterList } from '@mui/icons-material';
import { translateKey } from 'src/locales/translate';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export function ProfileMedicationsContent() {
  const theme = useTheme();
  const { profileId } = useParams<{ profileId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [medicationId, setMedicationId] = useState<string>('');
  const pendingFormData = useRef<MedicationsFormModalValuesProps | null>(null);
  const pendingMedicationId = useRef<string>('');
  const pendingLinkedReminder =
    useRef<GetProfileMedicationsDtoLinkedReminder | null>(null);
  const pendingSuccessAction = useRef<'add' | 'update' | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleOnRowClick = (medicationId: string) => {
    setMedicationId(medicationId);
    setIsModalOpen(true);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setMedicationId('');
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setMedicationId('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const { page, setPage, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const [filters, setFilters] = useState<MedicationFilters>(
    DEFAULT_MEDICATION_FILTERS,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sourceVisitId, setSourceVisitId] = useState('');

  const { data, isLoading } = useQuery(
    ['profile-medications', profileId, page, rowsPerPage, filters.statuses],
    () =>
      medicationApi.medicationControllerGetProfileMedications({
        profileId: profileId || '',
        page,
        limit: rowsPerPage,
        // An empty selection means everything, so send nothing.
        statuses: filters.statuses.length ? filters.statuses : undefined,
      }),
    {
      retry: 1,
      enabled: Boolean(profileId),
      // Keep the current page on screen while the next one loads.
      keepPreviousData: true,
    },
  );

  // Memoised because the submit handler closes over it to tell a status change
  // from an ordinary edit; a fresh array each render would rebuild it endlessly.
  const medications = useMemo(() => data?.data.data || [], [data]);
  const total = data?.data.total ?? 0;

  const selectedMedication = medicationId
    ? medications.find((m) => m.id === medicationId)
    : undefined;
  const linkedReminder: GetProfileMedicationsDtoLinkedReminder | null =
    selectedMedication?.linkedReminder ?? null;

  const buildReminderChannels = (
    emailEnabled: boolean,
    smsEnabled: boolean,
  ): CreateReminderDtoNotificationChannelsEnum[] => {
    const channels: CreateReminderDtoNotificationChannelsEnum[] = [];
    if (emailEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Email);
    if (smsEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Sms);
    return channels.length > 0
      ? channels
      : [CreateReminderDtoNotificationChannelsEnum.Email];
  };

  const buildReminderCreatePayload = (
    formData: MedicationsFormModalValuesProps,
    medId: string,
  ): CreateReminderDto => ({
    profileId: profileId || '',
    description: formData.reminderDescription,
    scheduledAt: (formData.reminderScheduledAt as Date).toISOString(),
    recurrenceType: formData.reminderRecurrenceType,
    customRecurrence:
      formData.reminderRecurrenceType === 'CUSTOM'
        ? {
            every: formData.reminderCustomEvery,
            unit: formData.reminderCustomUnit,
            ...(formData.reminderCustomDayOfWeek !== ''
              ? { dayOfWeek: Number(formData.reminderCustomDayOfWeek) }
              : {}),
            ...(formData.reminderCustomWeekOfMonth !== ''
              ? { weekOfMonth: formData.reminderCustomWeekOfMonth }
              : {}),
          }
        : undefined,
    notificationChannels: buildReminderChannels(
      formData.reminderEmailEnabled,
      formData.reminderSmsEnabled,
    ),
    medicationId: medId,
  });

  const buildReminderUpdatePayload = (
    formData: MedicationsFormModalValuesProps,
  ): UpdateReminderDto => ({
    description: formData.reminderDescription,
    scheduledAt: (formData.reminderScheduledAt as Date).toISOString(),
    recurrenceType: formData.reminderRecurrenceType,
    customRecurrence:
      formData.reminderRecurrenceType === 'CUSTOM'
        ? {
            every: formData.reminderCustomEvery,
            unit: formData.reminderCustomUnit,
            ...(formData.reminderCustomDayOfWeek !== ''
              ? { dayOfWeek: Number(formData.reminderCustomDayOfWeek) }
              : {}),
            ...(formData.reminderCustomWeekOfMonth !== ''
              ? { weekOfMonth: formData.reminderCustomWeekOfMonth }
              : {}),
          }
        : undefined,
    notificationChannels: buildReminderChannels(
      formData.reminderEmailEnabled,
      formData.reminderSmsEnabled,
    ),
  });

  const handlePostReminderSuccess = () => {
    queryClient.invalidateQueries(['profile-medications', profileId]);
    const action = pendingSuccessAction.current;
    enqueueSnackbar(
      action === 'add'
        ? translateKey('snackbar.medicationAdded')
        : translateKey('snackbar.medicationUpdated'),
      { variant: 'success' },
    );
    setIsModalOpen(false);
    pendingFormData.current = null;
    pendingSuccessAction.current = null;
    setTimeout(() => setMedicationId(''), 500);
  };

  const { mutate: createReminder } = useMutation({
    mutationFn: (payload: CreateReminderDto) =>
      reminderApi.reminderControllerCreateReminder({
        createReminderDto: payload,
      }),
    onSuccess: handlePostReminderSuccess,
  });

  const { mutate: updateReminder } = useMutation({
    mutationFn: ({
      reminderId,
      payload,
    }: {
      reminderId: string;
      payload: UpdateReminderDto;
    }) =>
      reminderApi.reminderControllerUpdateReminder({
        reminderId,
        updateReminderDto: payload,
      }),
    onSuccess: handlePostReminderSuccess,
  });

  const { mutate: deleteReminder } = useMutation({
    mutationFn: (reminderId: string) =>
      reminderApi.reminderControllerDeleteReminder({ reminderId }),
    onSuccess: handlePostReminderSuccess,
  });

  const { mutate: addMedication, isLoading: isAddingMedication } = useMutation({
    mutationFn: (payload: AddProfileMedicationDto) =>
      medicationApi.medicationControllerAddProfileMedication({
        profileId: profileId || '',
        addProfileMedicationDto: payload,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['profile-medications', profileId]);
      const formData = pendingFormData.current;
      if (formData?.reminderEnabled) {
        // The add endpoint answers with the whole list, newest first, so the
        // medication just created is its first row. This used to diff the
        // response against the list already on screen — which is now a single
        // page, and would have called anything from another page "new".
        const newMedication = response.data?.[0];
        if (newMedication?.id) {
          pendingSuccessAction.current = 'add';
          createReminder(
            buildReminderCreatePayload(formData, newMedication.id),
          );
          return;
        }
      }
      enqueueSnackbar(translateKey('snackbar.medicationAdded'), {
        variant: 'success',
      });
      setIsModalOpen(false);
      pendingFormData.current = null;
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.message || translateKey('snackbar.medicationAddError'),
        { variant: 'error' },
      );
    },
  });

  const { mutate: updateMedication, isLoading: isUpdatingMedication } =
    useMutation({
      mutationFn: (payload: UpdateProfileMedicationDto) =>
        medicationApi.medicationControllerUpdateProfileMedication({
          profileId: profileId || '',
          medicationId,
          updateProfileMedicationDto: payload,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries(['profile-medications', profileId]);
        const formData = pendingFormData.current;
        const currentMedicationId = pendingMedicationId.current;
        const currentLinkedReminder = pendingLinkedReminder.current;
        if (formData?.reminderEnabled) {
          pendingSuccessAction.current = 'update';
          if (currentLinkedReminder) {
            updateReminder({
              reminderId: currentLinkedReminder.id,
              payload: buildReminderUpdatePayload(formData),
            });
          } else {
            createReminder(
              buildReminderCreatePayload(formData, currentMedicationId),
            );
          }
          return;
        } else if (
          formData &&
          !formData.reminderEnabled &&
          currentLinkedReminder
        ) {
          pendingSuccessAction.current = 'update';
          deleteReminder(currentLinkedReminder.id);
          return;
        }
        enqueueSnackbar(translateKey('snackbar.medicationUpdated'), {
          variant: 'success',
        });
        setIsModalOpen(false);
        pendingFormData.current = null;
        setTimeout(() => setMedicationId(''), 500);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || translateKey('snackbar.medicationUpdateError'),
          { variant: 'error' },
        );
      },
    });

  const { mutate: deleteMedication, isLoading: isDeletingMedication } =
    useMutation({
      mutationFn: () =>
        medicationApi.medicationControllerDeleteProfileMedication({
          profileId: profileId || '',
          medicationId,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries(['profile-medications', profileId]);
        enqueueSnackbar(translateKey('snackbar.medicationDeleted'), {
          variant: 'success',
        });
        setIsModalOpen(false);
        setIsDeleteModalOpen(false);
        setTimeout(() => setMedicationId(''), 500);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || translateKey('snackbar.medicationDeleteError'),
          { variant: 'error' },
        );
      },
    });

  const { mutateAsync: changeMedicationStatus } = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ChangeMedicationStatusDto;
    }) =>
      medicationApi.medicationControllerChangeProfileMedicationStatus({
        profileId: profileId || '',
        medicationId: id,
        changeMedicationStatusDto: payload,
      }),
    onError: (error: any) => {
      enqueueSnackbar(
        error?.message || translateKey('snackbar.medicationUpdateError'),
        { variant: 'error' },
      );
    },
  });

  const handleOnSubmit = useCallback(
    async (formData: MedicationsFormModalValuesProps) => {
      pendingFormData.current = formData;
      pendingMedicationId.current = medicationId;
      pendingLinkedReminder.current = linkedReminder;
      const payload = buildMedicationProfilePayload(formData);
      if (medicationId) {
        // Stopping is not an edit: it closes the course with a date and a
        // reason, and lands in the medication's history.
        const current = medications.find((m) => m.id === medicationId);
        if (current && formData.status !== current.status) {
          await changeMedicationStatus({
            id: medicationId,
            payload: buildStatusChangePayload(formData),
          });
        }
        updateMedication(payload);
      } else {
        addMedication(payload);
      }
    },
    [
      addMedication,
      changeMedicationStatus,
      linkedReminder,
      medicationId,
      medications,
      updateMedication,
    ],
  );

  return (
    <>
      <Stack sx={CARD_HEADER_SX}>
        {isLoading ? (
          <Skeleton variant="rounded" height="24px" width="77px" />
        ) : (
          <Typography {...CARD_HEADER_TITLE_PROPS}>
            {translateKey('visit.tabMedications')}
          </Typography>
        )}
        <Stack direction="row" gap={1.5} alignItems="center">
          {!isLoading && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterList />}
              data-testid="medication-filters-toggle"
              onClick={() => setFiltersOpen((open) => !open)}
              color={
                filtersOpen || filters.statuses.length ? 'primary' : 'inherit'
              }
            >
              {translateKey('visit.historyFiltersTooltip')}
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" height="30px" width="77px" />
          ) : (
            <Button
              size="small"
              startIcon={<Iconify icon="eva:plus-fill" width={16} />}
              variant="contained"
              data-testid="medication-add-open"
              sx={{
                background: theme.palette.common.black,
                color: theme.palette.common.white,
              }}
              onClick={handleOpenModal}
            >
              {translateKey('common.add')}
            </Button>
          )}
        </Stack>
      </Stack>

      <ProfileMedicationsFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(0);
        }}
        isOpen={filtersOpen}
      />

      {isLoading ? (
        <ProfileMedicationsTableSkeleton />
      ) : (
        <ProfileMedicationsTable
          data={medications}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          onRowClick={handleOnRowClick}
          onVisitClick={setSourceVisitId}
        />
      )}
      <MedicationSourceVisitModal
        visitId={sourceVisitId}
        profileId={profileId}
        onClose={() => setSourceVisitId('')}
      />

      <MedicationFormModal
        profileId={profileId}
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={handleOpenDeleteModal}
        medication={selectedMedication}
        canEdit={selectedMedication?.canEdit ?? true}
        linkedReminder={linkedReminder}
        isCreating={isAddingMedication}
        isUpdating={isUpdatingMedication}
        isDeleting={isDeletingMedication}
      />
      <ProfileMedicationsDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        onDelete={deleteMedication}
        isLoading={isDeletingMedication}
      />
    </>
  );
}
